import AvatarCompany from "@/components/company/avatar-company";
import {
  CompanyPanelFooter,
  CompanySidePanelBody,
  type CompanyPanelMenu,
} from "@/components/company/company-preview-panel-content";
import CompanyTagActionsFooter, {
  type CompanyTagAction,
} from "@/components/company/company-tag-actions-footer";
import CompanyWebProfileView, {
  CompanyWebProfileFooter,
  CompanyWebSynthesisBox,
} from "@/components/company/company-web-profile-view";
import CompanyExternalLinks from "@/components/company/shared/company-external-links";
import CompanyPanelHeader from "@/components/company/shared/company-panel-header";
import SectionHeaderWithIcon from "@/components/company/shared/section-header-with-icon";
import SourceHostLink from "@/components/company/shared/source-host-link";
import CopyableField from "@/components/company/copyable-field";
import TopExecutiveSection from "@/components/company/top-executive-section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToastError } from "@/components/ui/use-toast";
import { useTeams } from "@/contexts/teams-provider";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useCompanyWebAvatar } from "@/hooks/useCompanyWebAvatar";
import { useCompanyWebPreview } from "@/hooks/useCompanyWebPreview";
import { useRecordCompanyPreviewView } from "@/hooks/useRecordCompanyPreviewView";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useTranslation } from "@/hooks/useTranslation";
import { webFigureTiles } from "@/components/company/company-web-figure-tiles";
import { websiteDomainOf } from "@/lib/company-mention-reliability";
import { buildCompanyTagLinks } from "@/lib/company-social-links";
import { companyTagContactDomain } from "@/lib/company-tag-creation";
import { sourceLink } from "@/lib/source-link";
import {
  isRunInProgress,
  type CompanyWebPreviewState,
} from "@/stores/company-web-preview-store";
import {
  companyPreviewLookupKey,
  type CompanyDTO,
  type CompanyTag,
  type CompanyWebFact,
  type CompanyWebHighlight,
  type CompanyWebKeyData,
} from "@shaarp/common";
import { useQueryClient } from "@tanstack/react-query";
import { GlobeIcon, MapPinIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

/** The pages behind the profile, from the run and from the tag, deduplicated. */
function distinctSources(urls: string[]) {
  const seen = new Set<string>();
  return urls.flatMap((url) => {
    const link = sourceLink(url);
    if (!link || seen.has(link.href)) return [];
    seen.add(link.href);
    return [link];
  });
}

/** The pages a profile still being written cites so far. */
function citedUrls(
  highlights: CompanyWebHighlight[],
  facts: CompanyWebFact[],
): string[] {
  return [
    ...highlights.flatMap((highlight) => highlight.urls),
    ...facts.map((fact) => fact.url),
  ];
}

/** "+N" chip of the header: opens the list of pages (tap, click or keyboard). */
function SourcesChip({ urls }: { urls: string[] }) {
  const { t } = useTranslation();
  const sources = distinctSources(urls);
  if (sources.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={t("company.webPreview.sourcesButton", {
            count: sources.length,
          })}
          className="h-8 rounded-lg px-2.5 text-xs font-semibold tabular-nums max-sm:min-h-11 max-sm:px-3"
        >
          +{sources.length}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <p className="mb-1 text-xs font-semibold text-muted-foreground">
          {t("company.webPreview.sources")}
        </p>
        <ul className="flex max-h-64 flex-col overflow-y-auto">
          {sources.map((source) => (
            <li key={source.href} className="min-w-0">
              <SourceHostLink link={source} className="flex py-1.5" />
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** Why the chat cites the company: only the tag knows it, base or not. */
function TagBrief({ brief }: { brief: string | undefined }) {
  const { t } = useTranslation();
  if (!brief) return null;
  return (
    <section>
      <SectionHeaderWithIcon title={t("company.tagPreview.brief")} />
      <p className="text-sm leading-relaxed">{brief}</p>
    </section>
  );
}

// The copy buttons grow to 44 px on touch screens.
const TOUCH_TARGET_CLASS = "max-sm:size-11";

/**
 * What the tag itself says: why it is cited and where. Its identifiers go to
 * the legal section and its LinkedIn page to the header's links; the address
 * the web found stands in for one the tag lacks.
 */
function TagDetails({
  tag,
  website,
  webAddress,
}: {
  tag: CompanyTag;
  website: string | null;
  webAddress: string | null;
}) {
  const { t } = useTranslation();
  const [copiedValue, copy] = useCopyToClipboard();
  const site = website ? sourceLink(`https://${website}`) : null;
  const address = tag.address || webAddress;

  return (
    <>
      {(site || address) && (
        <section className="flex flex-col gap-2">
          {site && (
            <CopyableField
              icon={GlobeIcon}
              label={t("company.webPreview.website")}
              value={site.host}
              copiedValue={copiedValue}
              onCopy={copy}
              href={site.href}
              buttonClassName={TOUCH_TARGET_CLASS}
            />
          )}
          {address && (
            <CopyableField
              icon={MapPinIcon}
              label={t("company.tagPreview.address")}
              value={address}
              copiedValue={copiedValue}
              onCopy={copy}
              buttonClassName={TOUCH_TARGET_CLASS}
            />
          )}
        </section>
      )}

      <TagBrief brief={tag.brief} />
    </>
  );
}

const COMPANY_IN_BASE_STALE_TIME_MS = 5 * 60 * 1000;

/** Keyed by team: switching teams must not serve another team's cached company. */
const companyInBaseKey = (teamId: string | undefined, companyId: string | null) => [
  "company-available",
  teamId,
  companyId,
];

/**
 * The company already in the base, read-only: `GET /company/available/:id` only
 * fetches the row, nothing is created or enriched by opening a panel.
 */
function useCompanyInBase(companyId: string | null): CompanyDTO | null {
  const toastError = useToastError();
  const { teamSelected } = useTeams();
  const { data, isError } = useApiQuery<CompanyDTO>({
    key: companyInBaseKey(teamSelected?.id, companyId),
    path: `/company/available/${companyId}`,
    staleTime: COMPANY_IN_BASE_STALE_TIME_MS,
    enabled: companyId !== null,
  });

  useEffect(() => {
    if (isError) toastError();
  }, [isError, toastError]);

  return companyId && data ? data : null;
}

/** The panel's scrolling content above its actions bar. */
function PanelLayout({
  children,
  footer,
}: {
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">{children}</div>
      {footer}
    </div>
  );
}

/**
 * The header of a company off the base, in the layout of the company panel's:
 * logo, name, brand and sources, then the synthesis and the links. While the
 * model writes, it fills in from what it has written, and what the stream
 * adds blurs in; a settled profile stays still.
 */
function CompanyTagHeader({
  tag,
  state,
  keyData,
  website,
  logo,
}: {
  tag: CompanyTag;
  state: CompanyWebPreviewState;
  keyData: Partial<CompanyWebKeyData> | undefined;
  website: string | null;
  /** A social account's picture, for a company with no site to take a logo from. */
  logo: string | null;
}) {
  const { t } = useTranslation();
  const { profile, partial } = state;
  const writing = partial?.status === "ok" ? partial : null;
  const isWriting = isRunInProgress(state);
  const brand = keyData?.brand ?? null;
  // A franchisee rarely has a site of its own: it shows its brand's logo, and
  // the badge says whose logo it is.
  const logoDomain = website ?? websiteDomainOf(keyData?.brandWebsite);
  const subtitle = [tag.city, keyData?.activityType ?? tag.activity]
    .filter(Boolean)
    .join(" · ");
  const links = buildCompanyTagLinks({
    tag,
    keyData,
    website,
    websiteLabel: (domain) =>
      t("components.dataTableCompanies.links.website", { domain }),
  });
  const sourceUrls = [
    ...(profile?.sources.map((source) => source.url) ??
      citedUrls(writing?.highlights ?? [], writing?.facts ?? [])),
    ...(tag.source ?? []),
  ];

  return (
    <CompanyPanelHeader
      avatar={
        <AvatarCompany
          className="w-14 h-14"
          company={{
            id: profile?.key ?? tag.name,
            name: tag.name,
            domain: logoDomain,
            logo,
            thumbnails: [],
          }}
        />
      }
      name={tag.name}
      subtitle={subtitle || undefined}
      badges={
        <>
          {brand && (
            <Badge
              variant="secondary"
              className={isWriting ? "blur-in-appear" : undefined}
            >
              {t("company.webPreview.brand", { brand })}
            </Badge>
          )}
          <SourcesChip urls={sourceUrls} />
        </>
      }
    >
      <CompanyWebSynthesisBox state={state} />
      <CompanyExternalLinks links={links} />
    </CompanyPanelHeader>
  );
}

/**
 * Side-panel content for a company mention opened from its tag, in the
 * language of the company panel: the same header, tiles and sections.
 *
 * The web profile streams in through `useCompanyWebPreview`. When the tag
 * resolves to a company already in base, that company's own panel content is
 * shown, the synthesis in its header, the web's figures among its tiles where
 * it lacks them, the other web sections above its own details and the
 * profile's footer at the very end, so it reads as one fiche where the base's figures win over web estimates, above
 * the actions bar of a company panel. A company not in base shows the same
 * actions bar: the first click on it saves the company, which the panel then
 * shows.
 *
 * Either way, a website — the base's, else the tag's or the settled profile's —
 * suggests a contact, as any company panel does; without one, nobody is
 * suggested: the people search reads a domain, not a SIRET.
 */
export default function CompanyTagPreview({ tag }: { tag: CompanyTag }) {
  const queryClient = useQueryClient();
  const { teamSelected } = useTeams();
  const { state, retry, attachCompany } = useCompanyWebPreview(tag);
  useRecordCompanyPreviewView(tag);
  const profile = state.profile;
  const companyId =
    profile?.companyId ??
    state.resolved?.companyId ??
    state.createdCompanyId ??
    null;
  const company = useCompanyInBase(companyId);
  // Only a settled profile's website and identifiers: one being written may
  // drop its website, or hold half a SIRET.
  const settledKeyData = profile?.status === "ok" ? profile.keyData : null;
  // While the model writes, the panel fills in from what it has written.
  const writingKeyData =
    state.partial?.status === "ok" ? state.partial.keyData : undefined;
  const keyData = settledKeyData ?? writingKeyData;
  const contactDomain = companyTagContactDomain({
    tag,
    keyData: settledKeyData,
  });
  // Off the base, a company with no site to take a logo from shows the
  // picture of its Facebook page or Instagram account.
  const socialAvatar = useCompanyWebAvatar(
    company ? null : (profile ?? null),
    !!(
      websiteDomainOf(settledKeyData?.website) ??
      tag.domain ??
      websiteDomainOf(settledKeyData?.brandWebsite)
    ),
  );

  // The menu the click that saved the company asked for, opened by its
  // actions bar once the panel shows it.
  const [savedMenu, setSavedMenu] = useState<{
    companyId: string;
    menu: CompanyPanelMenu;
  } | null>(null);

  const showSavedCompany = (saved: CompanyDTO, action: CompanyTagAction) => {
    // The save answered with the team's view of the company: no second read.
    queryClient.setQueryData(companyInBaseKey(teamSelected?.id, saved.id), saved);
    if (action !== "page") setSavedMenu({ companyId: saved.id, menu: action });
    attachCompany(saved.id);
  };

  if (company) {
    return (
      <PanelLayout
        footer={
          <CompanyPanelFooter
            company={company}
            openMenu={
              savedMenu?.companyId === company.id ? savedMenu.menu : undefined
            }
          />
        }
      >
        <CompanySidePanelBody
          company={company}
          // The base row's website wins; a row created from a SIRET often
          // has none, and then the one the tag or the web found stands in.
          topExecutiveSlot={
            company.domain || !contactDomain ? undefined : (
              <TopExecutiveSection domain={contactDomain} />
            )
          }
          headerSummary={<CompanyWebSynthesisBox state={state} />}
          webFigures={
            keyData &&
            webFigureTiles(keyData, tag, isRunInProgress(state))
          }
          bodyFooter={<CompanyWebProfileFooter state={state} />}
        >
          <CompanyWebProfileView
            state={state}
            company={company}
            tag={tag}
            showSynthesis={false}
            onRetry={retry}
            // The base panel has its own site, address and LinkedIn: only
            // the reason the chat cites the company is the tag's to add.
            afterSynthesis={<TagBrief brief={tag.brief} />}
          />
        </CompanySidePanelBody>
      </PanelLayout>
    );
  }

  // Model text: only a bare domain is shown, linked or searched for a logo.
  const website = websiteDomainOf(keyData?.website) ?? tag.domain ?? null;

  return (
    <PanelLayout
      footer={
        <CompanyTagActionsFooter
          // The panel steps from chip to chip without remounting: a save still
          // running belongs to the chip that started it.
          key={companyPreviewLookupKey(tag) ?? tag.name}
          tag={tag}
          onSaved={showSavedCompany}
        />
      }
    >
      <CompanyTagHeader
        tag={tag}
        state={state}
        keyData={keyData}
        website={website}
        logo={socialAvatar}
      />
      <div className="space-y-6 p-6 compact:space-y-4 compact:p-4">
        {/* Above the profile, not inside it: the profile's tree changes at
            every phase of the stream, and a remount would search again. */}
        {contactDomain && <TopExecutiveSection domain={contactDomain} />}
        <CompanyWebProfileView
          state={state}
          tag={tag}
          showSynthesis={false}
          onRetry={retry}
          afterSynthesis={
            <TagDetails
              tag={tag}
              website={website}
              webAddress={keyData?.address ?? null}
            />
          }
        />
      </div>
    </PanelLayout>
  );
}
